/**
 * Translation Context
 * Provides translation functionality across the application
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translationsApi } from '../api';

interface TranslationContextType {
  locale: string;
  translations: Record<string, string>;
  isLoading: boolean;
  t: (key: string, params?: Record<string, string | number> & { defaultValue?: string }) => string;
  changeLocale: (locale: string) => Promise<void>;
  availableLocales: { code: string; name: string; nativeName: string; flag: string }[];
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Minimal fallback translations - Only critical system messages
// All other translations should be loaded from the database
const defaultTranslations: Record<string, string> = {
  // Critical system messages only (as last resort fallback)
  'message.loading': 'Loading...',
  'message.error': 'Error',
  'message.not_found': 'Not Found',
  // Auth fallbacks
  'auth.or_continue_with': 'Or continue with',
  // Report user modal (fallback when API/cache not yet updated)
  'profile.report_user': 'Report user',
  'profile.report_reason': 'Reason',
  'profile.select_reason': 'Select a reason',
  'profile.report_details': 'Details (optional)',
  'profile.report_details_placeholder': 'Add any additional details...',
  'profile.submit_report': 'Submit report',
  'profile.report_reason_spam': 'Spam',
  'profile.report_reason_harassment': 'Harassment',
  'profile.report_reason_inappropriate': 'Inappropriate content',
  'profile.report_reason_fake_account': 'Fake account',
  'profile.report_reason_other': 'Other',
  'action.submitting': 'Submitting...',
  'ui.more_options_aria': 'More options',
  // Block / Unblock (profile)
  'profile.unblock': 'Unblock',
  'profile.user_unblocked': 'User unblocked.',
  'profile.unblock_error': 'Could not unblock user. Please try again.',
  'profile.block_user': 'Block user',
  'profile.block_user_confirm_title': 'Block this user?',
  'profile.block_user_confirm_message': 'They will no longer be able to message you or see your profile. You can unblock them later from your settings.',
  'profile.disconnect': 'Disconnect',
  // Search page
  'search.title': 'Search',
  'search.placeholder': 'Search discussions, blog, events, opportunities, people...',
  'search.search': 'Search',
  'search.no_results': 'No results found.',
  'search.enter_query': 'Enter a search term and press Search or Enter.',
  'search.type_all': 'All',
  'search.type_forum': 'Discussions',
  'search.type_blog': 'Blog',
  'search.type_posts': 'Posts',
  'search.type_events': 'Events',
  'search.type_opportunities': 'Opportunities',
  'search.type_users': 'People',
  'search.forum': 'Discussions',
  'search.blog': 'Blog',
  'search.posts': 'Posts',
  'search.events': 'Events',
  'search.opportunities': 'Opportunities',
  'search.users': 'People',
  'nav.search': 'Search',
  'search.shortcut_hint': 'Press Ctrl+K to open search anytime',
  'blog.like_this_post': 'Like this post',
  'blog.unlike_this_post': 'Unlike this post',
  'blog.min_read_count': ':count min read',
  'comment.like_this_comment': 'Like this comment',
  'comment.unlike_this_comment': 'Unlike this comment',
  'discovery.search_placeholder': 'Search by name, interests, skills...',
  'event_calendar.title': 'Event Calendar',
  'event_calendar.subtitle': 'View upcoming events by date',
  'event_calendar.click_event_hint': 'Click on any event to view details',
  'calendar.sun': 'Sun',
  'calendar.mon': 'Mon',
  'calendar.tue': 'Tue',
  'calendar.wed': 'Wed',
  'calendar.thu': 'Thu',
  'calendar.fri': 'Fri',
  'calendar.sat': 'Sat',
  'event_categories.tab_categories': 'Event Categories',
  'event_categories.tab_location_types': 'Location Types',
  'event.start_date_future': 'Start date must be at least 1 minute in the future',
  'event.end_date_after_start': 'End date must be after start date',
  'blog.my_drafts': 'My Drafts',
  'blog.all_posts': 'All Posts',
  'blog.my_drafts_description': 'Your unpublished blog posts',
  'blog.no_drafts': 'You have no draft posts.',
  'blog.editor.title_placeholder': 'Enter article title...',
  'blog.editor.featured_image': 'Featured Image',
  'blog.editor.no_image': 'No image',
  'blog.editor.upload_image': 'Upload Image',
  'blog.editor.change_image': 'Change Image',
  'blog.editor.uploading': 'Uploading...',
  'blog.editor.featured_image_hint': 'Recommended: 16:9 aspect ratio, max 5MB. JPG, PNG, or WebP formats.',
  'blog.editor.feature_post': 'Feature this post',
  'blog.editor.allow_comments': 'Allow comments',
  'blog.editor.content_placeholder': 'Write your article content here...',
  'blog.editor.insert_video': 'Insert video',
  'blog.editor.video_url_placeholder': 'Paste YouTube or Vimeo URL',
  'blog.editor.insert': 'Insert',
  // Timeline
  'timeline.whats_on_your_mind': "What's on your mind? Type @ to mention someone",
  'timeline.add_content_hint': 'Add a post, event, poll, and more',
  'timeline.trending_in_network': 'Trending in your network',
  'timeline.add_content': 'Add content',
  'timeline.status': 'Status',
  'timeline.status_posted': 'Status posted.',
  'forum.discussion': 'Forum discussion',
  'event.event': 'Event',
  'polls.poll': 'Poll',
  'blog.post': 'Blog post',
  'opportunity.opportunity': 'Opportunity',
  // Moderation (content moderation dashboard)
  'moderation.bulk_approve': 'Approve selected',
  'moderation.bulk_reject': 'Reject selected',
  'moderation.bulk_revision': 'Request revision',
  'moderation.flagged_for_review': 'Flagged for review',
  'moderation.no_flagged': 'No content flagged for review',
  'moderation.no_flagged_desc': 'Content that is flagged by the system or reported by users will appear here for review.',
};

// Kiswahili fallbacks (used when locale is 'sw' and key is missing from API)
const defaultTranslationsSw: Record<string, string> = {
  'timeline.whats_on_your_mind': 'Unafikiri nini? Andika @ kumtaja mtu',
  'timeline.add_content_hint': 'Ongeza chapisho, tukio, kura, na zaidi',
  'timeline.trending_in_network': 'Vinavyoongezeka kwenye mtandao wako',
  'timeline.add_content': 'Ongeza maudhui',
  'timeline.status': 'Hali',
  'timeline.status_posted': 'Hali imetumwa.',
  'forum.discussion': 'Majadiliano ya jukwaa',
  'event.event': 'Tukio',
  'polls.poll': 'Kura',
  'blog.post': 'Chapisho la blogu',
  'opportunity.opportunity': 'Fursa',
  'moderation.bulk_approve': 'Idhini zilizochaguliwa',
  'moderation.bulk_reject': 'Kataa zilizochaguliwa',
  'moderation.bulk_revision': 'Omba marekebisho',
  'moderation.flagged_for_review': 'Iliyowekwa alama kwa ukaguzi',
  'moderation.no_flagged': 'Hakuna maudhui yaliyowekwa alama kwa ukaguzi',
  'moderation.no_flagged_desc': 'Maudhui yaliyowekwa alama na mfumo au yaliyoripotiwa na watumiaji yataonekana hapa kwa ukaguzi.',
};

// Available locales (Kiswahili first)
const availableLocales = [
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇹🇿' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
];

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState<string>(() => {
    return localStorage.getItem('app_locale') || 'sw';
  });
  // Initialize with locale-specific defaults so UI shows correct language before API loads
  const [translations, setTranslations] = useState<Record<string, string>>(() => {
    const stored = localStorage.getItem('app_locale') || 'sw';
    return stored === 'sw' ? { ...defaultTranslationsSw } : { ...defaultTranslations };
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load translations
  const loadTranslations = useCallback(async (newLocale: string) => {
    try {
      setIsLoading(true);
      const localeDefaults = newLocale === 'sw' ? defaultTranslationsSw : defaultTranslations;
      const response = await translationsApi.getPublicTranslations(newLocale);
      if (response.success && response.data) {
        // Merge: locale-specific defaults first, then API (API takes precedence). Avoids showing raw keys.
        setTranslations({ ...localeDefaults, ...response.data });
        localStorage.setItem('app_locale', newLocale);
      } else {
        console.warn('Translation API response was not successful:', response);
        setTranslations(localeDefaults);
      }
    } catch (error: any) {
      console.error('Failed to load translations:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      setTranslations(newLocale === 'sw' ? defaultTranslationsSw : defaultTranslations);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load translations on mount and when locale changes
  useEffect(() => {
    loadTranslations(locale);
  }, [locale, loadTranslations]);

  // Translation function
  const t = useCallback((key: string, params?: Record<string, string | number> & { defaultValue?: string }): string => {
    const { defaultValue, ...replaceParams } = params ?? {};
    const localeDefaults: Record<string, string> = locale === 'sw' ? defaultTranslationsSw : {};
    // Try loaded translations, then locale-specific defaults (sw), then English defaults, then defaultValue, then key
    let translation =
      translations[key] ||
      localeDefaults[key] ||
      defaultTranslations[key] ||
      (defaultValue !== undefined ? defaultValue : key);

    // Replace parameters in translation (e.g. :count)
    if (replaceParams && Object.keys(replaceParams).length > 0) {
      Object.entries(replaceParams).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(new RegExp(`:${paramKey}`, 'g'), String(paramValue));
      });
    }

    return translation;
  }, [translations, locale]);

  // Change locale
  const changeLocale = useCallback(async (newLocale: string) => {
    if (newLocale !== locale) {
      setLocale(newLocale);
      await loadTranslations(newLocale);
    }
  }, [locale, loadTranslations]);

  const value: TranslationContextType = {
    locale,
    translations,
    isLoading,
    t,
    changeLocale,
    availableLocales,
  };

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Export both named and default for compatibility
export { TranslationContext };
export default TranslationContext;

